<!-- ══════════════════════════════════════════════════════════════════════════
     Job Form AI Onboarding – 2 écrans
     Hérite de JobFormFuseComponent / JobFormComponent
     ══════════════════════════════════════════════════════════════════════════ -->

<!-- ═══════════════════════════════════════════════════════════════════════
     ÉCRAN 1 : Statut + Champs métier/secteur
     ═══════════════════════════════════════════════════════════════════════ -->
@if (currentScreen() === 1) {
  <div class="space-y-6 screen-animate">

    <!-- Titre + sous-titre -->
    @if (showTitle) {
      <div>
        <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-3" translate>
          examination-ai.onboarding.job.title
        </h1>
        <p class="text-base text-gray-600 leading-relaxed" translate>
          examination-ai.onboarding.job.subtitle
        </p>
      </div>

      <!-- Hint -->
      <div class="hint-box" translate>
        examination-ai.onboarding.job.hint
      </div>
    }

    <!-- ── BLOC 1 : Statut (grille 2×2) ── -->
    <div>
      <div class="bloc-label">
        <span translate>examination-ai.onboarding.job.bloc1.label</span>
        <small>&nbsp;—&nbsp;<span translate>examination-ai.onboarding.job.bloc1.required</span></small>
      </div>

      <!-- Radio group caché (pour le form hérité) -->
      <mat-radio-group
        class="invisible absolute w-0 h-0 pointer-events-none"
        [formControl]="businessForm.controls.job_situation"
        #jobRadioGroup="matRadioGroup">
        @for (situation of job_situation_options; track situation.value) {
          <mat-radio-button [value]="situation.value"></mat-radio-button>
        }
      </mat-radio-group>

      <div class="statuts-grid">
        @for (statut of statutOptions; track statut.value) {
          <button
            type="button"
            class="statut-btn"
            [class.selected]="selectedStatut() === statut.value"
            (click)="selectStatut(statut.value)">
            <span class="statut-icon">{{ statut.emoji }}</span>
            <span translate>{{ statut.labelKey }}</span>
          </button>
        }
      </div>
    </div>

    <!-- ── BLOC 2 : Contexte (conditionnel, même écran) ── -->
    @if (showBloc2()) {
      <hr class="divider">

      <div class="bloc-contexte-animate">
        <!-- Titre et sous-titre adaptatifs -->
        <div class="contexte-title" [translate]="contexteTitle()"></div>
        <div class="contexte-subtitle" [translate]="contexteSubtitle()"></div>

        <!-- Champs métier + secteur (autocomplete hérités) -->
        @if (showJobFields()) {
          <div class="fields-grid">

            <!-- Champ Métier -->
            <div class="field-group">
              <label class="field-label" [translate]="jobLabelKey()"></label>

              @if (jobs && !showJobSuggestionField && !suggestedJob) {
                <mat-form-field appearance="outline" floatLabel="never" class="w-full">
                  <mat-icon class="icon-size-5" matPrefix>search</mat-icon>
                  <input
                    #jobInput
                    type="text"
                    matInput
                    [placeholder]="'job.form.fuse.jobPlaceholder' | translate"
                    [formControl]="searchJobControl"
                    [matAutocomplete]="autoJob"
                    #jobAutocompleteTrigger="matAutocompleteTrigger"
                    (input)="onJobTextInput()"
                    (blur)="onJobInputBlur()" />

                  <mat-autocomplete #autoJob="matAutocomplete" [displayWith]="displayJob">
                    @for (job of jobs; track job.id) {
                      <mat-option
                        [value]="job"
                        (onSelectionChange)="selectJob(job, $event)">
                        <span [innerHTML]="highlightMatch(job.name, searchJobControl.value)"></span>
                      </mat-option>
                    }

                    @if (shouldInviteJobSuggestion() && !showJobSuggestionField && (searchJobControl.value?.name || searchJobControl.value)) {
                      <hr class="border-black my-2">
                      <mat-option
                        class="job-suggest-option"
                        (onSelectionChange)="onJobSuggestOptionSelection($event)">
                        <div class="flex items-center gap-2">
                          <mat-icon class="icon-size-5 text-blue-600">add_circle_outline</mat-icon>
                          <span class="text-blue-700">
                            <span translate>lingueopro.job.form.suggestButton</span>
                            <span class="font-semibold"> {{ searchJobControl.value?.name || searchJobControl.value || '' }}</span>
                          </span>
                        </div>
                      </mat-option>
                    }
                  </mat-autocomplete>

                  @if (searchJobControl.touched && searchJobControl.hasError('notSelected')) {
                    <mat-error translate>examination-ai.onboarding.job.field.job.not-selected</mat-error>
                  }
                  @if (searchJobControl.touched && searchJobControl.hasError('required')) {
                    <mat-error translate>examination-ai.onboarding.job.field.job.required</mat-error>
                  }
                </mat-form-field>
              }

              <!-- Suggestion métier -->
              @if (suggestedJobControl.value) {
                <div class="suggestion-display">
                  <span translate>examination-ai.onboarding.job.suggestion.job</span>
                  <span class="font-semibold">{{ suggestedJobControl.value }}</span>
                </div>
              }
            </div>

            <!-- Champ Secteur -->
            <div class="field-group">
              <label class="field-label" [translate]="sectorLabelKey()"></label>

              @if (businesses && !showBusinessSuggestionField && !suggestedBusiness) {
                <mat-form-field appearance="outline" floatLabel="never" class="w-full">
                  <mat-icon class="icon-size-5" matPrefix>search</mat-icon>
                  <input
                    #businessInput
                    type="text"
                    matInput
                    [placeholder]="'job.form.fuse.businessPlaceholder' | translate"
                    [formControl]="searchBusinessControl"
                    [matAutocomplete]="autoBusiness"
                    #businessAutocompleteTrigger="matAutocompleteTrigger"
                    (input)="onBusinessTextInput()"
                    (blur)="onBusinessInputBlur()" />

                  <mat-autocomplete #autoBusiness="matAutocomplete" [displayWith]="displayBusiness">
                    @for (business of businesses; track business.id) {
                      <mat-option
                        [value]="business"
                        (onSelectionChange)="selectBusiness(business, $event)">
                        <span [innerHTML]="highlightMatch(business.name, searchBusinessControl.value)"></span>
                      </mat-option>
                    }

                    @if (shouldInviteBusinessSuggestion() && !showBusinessSuggestionField && (searchBusinessControl.value?.name || searchBusinessControl.value)) {
                      <hr class="border-black my-2">
                      <mat-option
                        class="job-suggest-option"
                        (onSelectionChange)="onBusinessSuggestOptionSelection($event)">
                        <div class="flex items-center gap-2">
                          <mat-icon class="icon-size-5 text-blue-600">add_circle_outline</mat-icon>
                          <span class="text-blue-700">
                            <span translate>lingueopro.job.form.suggestButton</span>
                            <span class="font-semibold"> {{ searchBusinessControl.value?.name || searchBusinessControl.value || '' }}</span>
                          </span>
                        </div>
                      </mat-option>
                    }
                  </mat-autocomplete>

                  @if (searchBusinessControl.touched && searchBusinessControl.hasError('notSelected')) {
                    <mat-error translate>examination-ai.onboarding.job.field.sector.not-selected</mat-error>
                  }
                  @if (searchBusinessControl.touched && searchBusinessControl.hasError('required')) {
                    <mat-error translate>examination-ai.onboarding.job.field.sector.required</mat-error>
                  }
                </mat-form-field>
              }

              <!-- Suggestion secteur -->
              @if (suggestedBusinessControl.value) {
                <div class="suggestion-display">
                  <span translate>examination-ai.onboarding.job.suggestion.sector</span>
                  <span class="font-semibold">{{ suggestedBusinessControl.value }}</span>
                </div>
              }
            </div>

          </div>
        }
      </div>
    }

    <!-- Bouton Suivant écran 1 → écran 2 -->
    @if (showButton) {
      <div class="flex justify-end pt-4">
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="!canSubmitScreen1()"
          (click)="goToScreen2()"
          class="px-8">
          <span translate>examination-ai.common.next-button</span>
        </button>
      </div>
    }
  </div>
}


<!-- ═══════════════════════════════════════════════════════════════════════
     ÉCRAN 2 : Entreprise (nom + site internet, requis)
     ═══════════════════════════════════════════════════════════════════════ -->
@if (currentScreen() === 2) {
  <div class="space-y-6 screen-animate">

    <!-- Titre + sous-titre -->
    <div>
      <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-3" translate>
        examination-ai.onboarding.job.screen2.company.title
      </h1>
      <p class="text-base text-gray-600 leading-relaxed" [translate]="screen2Subtitle()"></p>
    </div>

    <!-- Hint -->
    <div class="hint-box" [translate]="screen2Hint()"></div>

    <div class="fields-grid">
      <!-- Nom de l'entreprise -->
      <div class="field-group">
        <label class="field-label" translate>examination-ai.onboarding.job.company.name</label>
        <mat-form-field appearance="outline" floatLabel="never" class="w-full">
          <input
            type="text"
            matInput
            [placeholder]="'examination-ai.onboarding.job.company.name.placeholder' | translate"
            [formControl]="companyNameControl" />
          @if (companyNameControl.hasError('required')) {
            <mat-error translate>examination-ai.onboarding.job.company.name.required</mat-error>
          }
        </mat-form-field>
      </div>

      <!-- Site internet -->
      <div class="field-group">
        <label class="field-label" translate>examination-ai.onboarding.job.company.website</label>
        <mat-form-field appearance="outline" floatLabel="never" class="w-full">
          <input
            type="text"
            matInput
            [placeholder]="'examination-ai.onboarding.job.company.website.placeholder' | translate"
            [formControl]="companyWebsiteControl" />
          @if (companyWebsiteControl.hasError('required')) {
            <mat-error translate>examination-ai.onboarding.job.company.website.required</mat-error>
          }
          @if (companyWebsiteControl.hasError('invalidUrl')) {
            <mat-error translate>examination-ai.onboarding.job.company.website.invalid</mat-error>
          }
        </mat-form-field>
      </div>
    </div>
  </div>
}
